import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronDown, LogOut, Menu, User2 } from "lucide-react";

import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
    SheetClose,
} from "@/components/ui/sheet";

// Pages publiques (servies par le backend, lisibles par les robots) montrées
// aux visiteurs non connectés, dans l'en-tête et le menu du téléphone.
const LIENS_PUBLICS = [
    { href: "/histoires", label: "Les histoires", testid: "nav-histoires" },
    { href: "/podcast", label: "Podcast", testid: "nav-podcast", large: true },
    { href: "/entreprises", label: "Entreprises", testid: "nav-entreprises" },
    { href: "/annonceurs", label: "Annonceurs", testid: "nav-annonceurs", large: true },
];

const prenom = (nom) => String(nom || "").trim().split(/\s+/)[0] || "Mon compte";

// Menu du compte : un simple volet (bouton + liste de liens), sans bibliothèque de menus, pour ne
// rien ajouter au JavaScript de l'accueil. Se ferme avec Échap, un clic ailleurs ou un choix.
const MenuCompte = ({ user, onLogout }) => {
    const [ouvert, setOuvert] = useState(false);
    const racine = useRef(null);
    const location = useLocation();
    useEffect(() => setOuvert(false), [location.pathname, location.search]);
    useEffect(() => {
        if (!ouvert) return undefined;
        const dehors = (e) => racine.current && !racine.current.contains(e.target) && setOuvert(false);
        const echap = (e) => {
            if (e.key === "Escape") {
                setOuvert(false);
                const bouton = racine.current && racine.current.querySelector("button");
                if (bouton) bouton.focus();
            }
        };
        document.addEventListener("mousedown", dehors);
        document.addEventListener("keydown", echap);
        return () => {
            document.removeEventListener("mousedown", dehors);
            document.removeEventListener("keydown", echap);
        };
    }, [ouvert]);
    const lien = "block px-3 py-2 text-sm hover:bg-sand/60 focus-visible:bg-sand/60 outline-none";
    return (
        <div className="relative" ref={racine}>
            <button
                type="button"
                data-testid="user-chip"
                aria-expanded={ouvert}
                aria-controls="menu-compte"
                onClick={() => setOuvert((o) => !o)}
                className="flex items-center gap-2 px-3 py-1.5 border border-rule hover:border-ink transition-colors"
            >
                <User2 className="w-4 h-4" aria-hidden="true" />
                <span className="text-sm">{prenom(user.name)}</span>
                {user.is_premium ? (
                    <span
                        data-testid="premium-badge"
                        className="ml-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-terracotta"
                    >
                        Premium
                    </span>
                ) : null}
                <ChevronDown className="w-3.5 h-3.5 opacity-60" aria-hidden="true" />
            </button>
            {ouvert ? (
                <div
                    id="menu-compte"
                    data-testid="user-menu"
                    className="absolute right-0 top-full mt-2 min-w-[11rem] z-50 border border-ink bg-[#fffef9] p-1 shadow-[3px_3px_0_0_#17251d]"
                >
                    <Link to="/compte" data-testid="menu-account" className={lien}>
                        Mon compte
                    </Link>
                    <Link to="/sur-mesure" data-testid="nav-custom" className={lien}>
                        Sur mesure
                    </Link>
                    {user.is_admin ? (
                        <Link to="/admin" data-testid="nav-admin" className={lien}>
                            Atelier
                        </Link>
                    ) : null}
                    <button
                        type="button"
                        data-testid="logout-btn"
                        onClick={() => {
                            setOuvert(false);
                            onLogout();
                        }}
                        className={`${lien} w-full text-left flex items-center gap-2 border-t border-rule`}
                    >
                        <LogOut className="w-4 h-4" aria-hidden="true" />
                        Se déconnecter
                    </button>
                </div>
            ) : null}
        </div>
    );
};

const Header = () => {
    const { user, status, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isLanding = location.pathname === "/";
    // Accueil, « Aujourd'hui », bibliothèque, histoire et « Ma mémoire » partagent l'en-tête léger de l'accueil (carte.css, préfixe cj-).
    const cj =
        isLanding ||
        ["/aujourdhui", "/bibliotheque", "/dashboard"].includes(location.pathname) ||
        location.pathname.startsWith("/lecon/");
    const surAccueil = isLanding && new URLSearchParams(location.search).get("accueil") === "1";
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleLogout = () => {
        setMobileOpen(false);
        // Naviguer AVANT logout() pour éviter que ProtectedRoute n'intercepte
        // le passage status->guest et redirige vers /login.
        navigate("/", { replace: true });
        logout();
    };

    const closeMobile = () => setMobileOpen(false);

    // L'en-tête reste collé en haut au défilement. Sa hauteur réelle est
    // publiée dans --hauteur-entete : l'atelier colle son menu juste dessous,
    // et les ancres (#tarifs…) s'arrêtent sous l'en-tête au lieu de dessous.
    const entete = useRef(null);
    useEffect(() => {
        const el = entete.current;
        if (!el) return undefined;
        const publier = () =>
            document.documentElement.style.setProperty(
                "--hauteur-entete",
                `${Math.round(el.getBoundingClientRect().height)}px`,
            );
        publier();
        if (typeof ResizeObserver === "undefined") return undefined;
        const obs = new ResizeObserver(publier);
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    return (
        <header
            ref={entete}
            data-testid="app-header"
            className={
                cj
                    ? "cj-entete bg-parchment z-40"
                    : "border-b border-rule bg-parchment shadow-[0_1px_0_0_rgba(28,27,26,0.04)] sticky top-0 z-40"
            }
        >
            <div className={`${cj ? "cj-w" : "container-edit"} flex items-center justify-between ${cj ? "py-5 md:pt-7" : "py-5"}`}>
                <Link
                    to={status === "authed" ? "/aujourdhui" : "/"}
                    data-testid="logo-link"
                    className="flex items-baseline gap-2 group"
                >
                    <span className="lsv-logo text-[27px] md:text-[34px] leading-none text-ink">
                        Le savez-<em>vous&#8239;?</em>
                    </span>
                </Link>

                {/* Desktop nav */}
                <nav className="hidden md:flex items-center gap-5 xl:gap-8 whitespace-nowrap">
                    {status === "authed" ? (
                        <>
                            <NavLink
                                to="/aujourdhui"
                                data-testid="nav-today"
                                className={({ isActive }) =>
                                    `text-sm tracking-wide transition-colors ${isActive ? "text-terracotta font-semibold" : "text-ink/70 hover:text-ink"}`
                                }
                            >
                                Aujourd&apos;hui
                            </NavLink>
                            <NavLink
                                to="/bibliotheque"
                                data-testid="nav-library"
                                className={({ isActive }) =>
                                    `text-sm tracking-wide transition-colors ${isActive ? "text-terracotta font-semibold" : "text-ink/70 hover:text-ink"}`
                                }
                            >
                                Bibliothèque
                            </NavLink>
                            <NavLink
                                to="/dashboard"
                                data-testid="nav-dashboard"
                                className={({ isActive }) =>
                                    `text-sm tracking-wide transition-colors ${isActive ? "text-terracotta font-semibold" : "text-ink/70 hover:text-ink"}`
                                }
                            >
                                Ma mémoire
                            </NavLink>
                            {/* L'accueil public (la redirection « connecté → Aujourd'hui » ne joue pas avec ?accueil=1). */}
                            <Link
                                to="/?accueil=1"
                                data-testid="nav-home"
                                aria-current={surAccueil ? "page" : undefined}
                                className={`text-sm tracking-wide transition-colors ${surAccueil ? "text-terracotta font-semibold" : "text-ink/70 hover:text-ink"}`}
                            >
                                Accueil du site
                            </Link>
                        </>
                    ) : (
                        <>
                            {/* Pages publiques servies par le backend (HTML complet) :
                                liens classiques, pas des routes React. Toujours visibles. */}
                            {LIENS_PUBLICS.filter((l) => !isLanding || l.testid === "nav-histoires" || l.testid === "nav-podcast").map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    data-testid={l.testid}
                                    className={`${l.large && !isLanding ? "hidden lg:inline" : ""} text-sm tracking-wide text-ink/70 hover:text-ink`}
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href="#comment"
                                data-testid="nav-howitworks"
                                className={`${isLanding ? "hidden xl:inline" : "hidden"} text-sm tracking-wide text-ink/70 hover:text-ink`}
                            >
                                Comment ça marche
                            </a>
                            <a
                                href="#tarifs"
                                data-testid="nav-pricing"
                                className={`${isLanding ? "hidden xl:inline" : "hidden"} text-sm tracking-wide text-ink/70 hover:text-ink`}
                            >
                                Tarifs
                            </a>
                            <a
                                href="#faq"
                                data-testid="nav-faq"
                                className={`${isLanding ? "hidden xl:inline" : "hidden"} text-sm tracking-wide text-ink/70 hover:text-ink`}
                            >
                                Questions
                            </a>
                            <Link
                                to="/register"
                                data-testid="nav-register"
                                className="text-sm tracking-wide text-terracotta hover:text-ink font-semibold"
                                style={{ display: isLanding ? "inline" : "none" }}
                            >
                                S&apos;inscrire
                            </Link>
                        </>
                    )}
                </nav>

                {/* Desktop right cluster */}
                <div className="hidden md:flex items-center gap-3">
                    {status === "authed" && user ? (
                        <>
                            <MenuCompte user={user} onLogout={handleLogout} />
                        </>
                    ) : status === "guest" ? (
                        <Link
                            to="/login"
                            data-testid="header-login-btn"
                            className="btn-ghost text-sm"
                        >
                            Se connecter
                        </Link>
                    ) : null}
                </div>

                {/* Mobile hamburger */}
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                    <SheetTrigger asChild>
                        <button
                            data-testid="mobile-menu-trigger"
                            aria-label="Ouvrir le menu"
                            className="md:hidden btn-ghost p-2"
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                    </SheetTrigger>
                    <SheetContent
                        side="right"
                        data-testid="mobile-menu"
                        className="bg-parchment border-l border-rule p-0 w-3/4 sm:max-w-sm"
                    >
                        <SheetHeader className="px-6 py-5 border-b border-rule text-left">
                            <SheetTitle className="lsv-logo text-2xl">
                                Le savez-<em>vous&#8239;?</em>
                            </SheetTitle>
                            <SheetDescription className="sr-only">
                                Menu de navigation principal
                            </SheetDescription>
                        </SheetHeader>
                        <nav className="flex flex-col px-6 py-6 gap-1">
                            {status === "authed" ? (
                                <>
                                    {user ? (
                                        <div
                                            data-testid="mobile-user-chip"
                                            className="flex items-center gap-2 mb-4 pb-4 border-b border-rule"
                                        >
                                            <User2 className="w-4 h-4" />
                                            <span className="text-sm">{user.name}</span>
                                            {user.is_premium ? (
                                                <span className="ml-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-terracotta">
                                                    Premium
                                                </span>
                                            ) : null}
                                        </div>
                                    ) : null}
                                    <SheetClose asChild>
                                        <NavLink
                                            to="/aujourdhui"
                                            data-testid="mobile-nav-today"
                                            onClick={closeMobile}
                                            className="py-3 text-base text-ink hover:text-terracotta border-b border-rule"
                                        >
                                            Aujourd&apos;hui
                                        </NavLink>
                                    </SheetClose>
                                    <SheetClose asChild>
                                        <NavLink
                                            to="/bibliotheque"
                                            data-testid="mobile-nav-library"
                                            onClick={closeMobile}
                                            className="py-3 text-base text-ink hover:text-terracotta border-b border-rule"
                                        >
                                            Bibliothèque
                                        </NavLink>
                                    </SheetClose>
                                    <SheetClose asChild>
                                        <NavLink
                                            to="/sur-mesure"
                                            data-testid="mobile-nav-custom"
                                            onClick={closeMobile}
                                            className="py-3 text-base text-ink hover:text-terracotta border-b border-rule"
                                        >
                                            Sur mesure
                                        </NavLink>
                                    </SheetClose>
                                    <SheetClose asChild>
                                        <NavLink
                                            to="/dashboard"
                                            data-testid="mobile-nav-dashboard"
                                            onClick={closeMobile}
                                            className="py-3 text-base text-ink hover:text-terracotta border-b border-rule"
                                        >
                                            Ma mémoire
                                        </NavLink>
                                    </SheetClose>
                                    <SheetClose asChild>
                                        <Link
                                            to="/?accueil=1"
                                            data-testid="mobile-nav-home"
                                            onClick={closeMobile}
                                            className="py-3 text-base text-ink hover:text-terracotta border-b border-rule"
                                        >
                                            Accueil du site
                                        </Link>
                                    </SheetClose>
                                    <SheetClose asChild>
                                        <NavLink
                                            to="/compte"
                                            data-testid="mobile-nav-account"
                                            onClick={closeMobile}
                                            className="py-3 text-base text-ink hover:text-terracotta border-b border-rule"
                                        >
                                            Mon compte
                                        </NavLink>
                                    </SheetClose>
                                    {user?.is_admin ? (
                                        <SheetClose asChild>
                                            <NavLink
                                                to="/admin"
                                                data-testid="mobile-nav-admin"
                                                onClick={closeMobile}
                                                className="py-3 text-base text-ink hover:text-terracotta border-b border-rule"
                                            >
                                                Atelier
                                            </NavLink>
                                        </SheetClose>
                                    ) : null}
                                    <button
                                        onClick={handleLogout}
                                        data-testid="mobile-logout-btn"
                                        className="mt-6 btn-secondary w-full inline-flex items-center justify-center gap-2"
                                    >
                                        <LogOut className="w-4 h-4" />
                                        Se déconnecter
                                    </button>
                                </>
                            ) : (
                                <>
                                    {LIENS_PUBLICS.map((l) => (
                                        <a
                                            key={l.href}
                                            href={l.href}
                                            data-testid={`mobile-${l.testid}`}
                                            className="py-3 text-base text-ink hover:text-terracotta border-b border-rule"
                                        >
                                            {l.label}
                                        </a>
                                    ))}
                                    {isLanding ? (
                                        <>
                                            <SheetClose asChild>
                                                <a
                                                    href="#comment"
                                                    data-testid="mobile-nav-howitworks"
                                                    className="py-3 text-base text-ink hover:text-terracotta border-b border-rule"
                                                >
                                                    Comment ça marche
                                                </a>
                                            </SheetClose>
                                            <SheetClose asChild>
                                                <a
                                                    href="#tarifs"
                                                    data-testid="mobile-nav-pricing"
                                                    className="py-3 text-base text-ink hover:text-terracotta border-b border-rule"
                                                >
                                                    Tarifs
                                                </a>
                                            </SheetClose>
                                            <SheetClose asChild>
                                                <a
                                                    href="#faq"
                                                    data-testid="mobile-nav-faq"
                                                    className="py-3 text-base text-ink hover:text-terracotta border-b border-rule"
                                                >
                                                    Questions
                                                </a>
                                            </SheetClose>
                                            <SheetClose asChild>
                                                <Link
                                                    to="/register"
                                                    data-testid="mobile-nav-register"
                                                    onClick={closeMobile}
                                                    className="py-3 text-base text-terracotta font-semibold border-b border-rule"
                                                >
                                                    S&apos;inscrire
                                                </Link>
                                            </SheetClose>
                                        </>
                                    ) : null}
                                    <SheetClose asChild>
                                        <Link
                                            to="/login"
                                            data-testid="mobile-login-btn"
                                            className="mt-6 btn-secondary w-full text-center"
                                        >
                                            Se connecter
                                        </Link>
                                    </SheetClose>
                                </>
                            )}
                        </nav>
                    </SheetContent>
                </Sheet>
            </div>
        </header>
    );
};

export default Header;
